export function parseOperationsDate(value: unknown, fallback: string): string {
  return typeof value === 'string' && isCalendarDate(value) ? value : fallback;
}

/**
 * Eine Zeitzone, die der Browser kennt; sonst die des Property-Standards. Der Server meldet eine
 * ungültige Objektzeitzone selbst (409), die Seite soll daran nur nicht abstürzen.
 */
export function safeTimeZone(timeZone: string | null | undefined): string {
  if (!timeZone) return 'Europe/Berlin';
  try {
    new Intl.DateTimeFormat('en', { timeZone });
    return timeZone;
  } catch {
    return 'Europe/Berlin';
  }
}

export function todayInTimeZone(timeZone: string, now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

export function addCalendarDays(value: string, days: number): string {
  const date = new Date(
    Date.UTC(Number(value.slice(0, 4)), Number(value.slice(5, 7)) - 1, Number(value.slice(8, 10))),
  );
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function startOfCalendarWeek(value: string): string {
  const weekday = new Date(`${value}T00:00:00Z`).getUTCDay();
  return addCalendarDays(value, -(weekday === 0 ? 6 : weekday - 1));
}

export function calendarWeek(value: string): string[] {
  const monday = startOfCalendarWeek(value);
  return Array.from({ length: 7 }, (_, index) => addCalendarDays(monday, index));
}

export function formatCalendarDate(value: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${value}T00:00:00Z`));
}

function isCalendarDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.valueOf()) && parsed.toISOString().slice(0, 10) === value;
}

/** Minuten seit Mitternacht in der Objektzeit – für die Jetzt-Linie auf der Tagesachse. */
export function nowMinutesInTimeZone(timeZone: string, now = new Date()): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((item) => item.type === type)?.value ?? 0);
  return part('hour') * 60 + part('minute');
}
